import { Box } from "@mui/material";
import ScrollTopButton from "components/ScrollTopButton";
import { ComponentType, RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const defaultMaxHeightOffset = 20;
type ResponsiveMaxHeightOffset = number | Partial<Record<"xs" | "sm" | "md" | "lg" | "xl", number>>;

interface WithHeightFitContentParams {
  refProp: RefObject<HTMLDivElement>;
  withoutScrollTopButton?: boolean;
  additionalMaxHeightOffset?: ResponsiveMaxHeightOffset;
  recalculateSignal?: unknown;
}

const getMaxHeightValue = (top: number, additionalMaxHeightOffset?: ResponsiveMaxHeightOffset) => {
  const buildCalc = (offset?: number) =>
    `calc(100vh - ${top}px - ${defaultMaxHeightOffset}px${offset ? ` - ${offset}px` : ""})`;

  if (typeof additionalMaxHeightOffset === "number" || additionalMaxHeightOffset === undefined) {
    return buildCalc(additionalMaxHeightOffset);
  }

  return Object.entries(additionalMaxHeightOffset).reduce<Record<string, string>>((acc, [breakpoint, offset]) => {
    if (typeof offset === "number") {
      acc[breakpoint] = buildCalc(offset);
    }

    return acc;
  }, {});
};

const withHeightFitContent = <T extends object>(Component: ComponentType<T>) => {
  // eslint-disable-next-line react/function-component-definition, react/display-name
  return ({
    refProp: ref,
    withoutScrollTopButton = false,
    additionalMaxHeightOffset,
    recalculateSignal,
    ...props
  }: T & WithHeightFitContentParams) => {
    const [top, setTop] = useState(0);
    const recalculateRafIdRef = useRef<number | null>(null);
    const recalculateTimeoutIdRef = useRef<number | null>(null);

    const clearScheduledRecalculate = () => {
      if (recalculateRafIdRef.current !== null) {
        cancelAnimationFrame(recalculateRafIdRef.current);
        recalculateRafIdRef.current = null;
      }

      if (recalculateTimeoutIdRef.current !== null) {
        window.clearTimeout(recalculateTimeoutIdRef.current);
        recalculateTimeoutIdRef.current = null;
      }
    };

    const updateTop = useCallback(() => {
      const nextTop = Math.round(ref.current?.getBoundingClientRect()?.top ?? 0);
      setTop((prevTop) => (prevTop === nextTop ? prevTop : nextTop));
    }, [ref]);

    useLayoutEffect(() => {
      if (!ref.current) return;

      updateTop();

      const ro = new ResizeObserver(() => {
        requestAnimationFrame(updateTop);
      });

      ro.observe(ref.current);
      window.addEventListener("resize", updateTop);

      // eslint-disable-next-line consistent-return
      return () => {
        clearScheduledRecalculate();
        ro.disconnect();
        window.removeEventListener("resize", updateTop);
      };
    }, [ref, updateTop]);

    useEffect(() => {
      clearScheduledRecalculate();

      recalculateRafIdRef.current = requestAnimationFrame(updateTop);
      recalculateTimeoutIdRef.current = window.setTimeout(updateTop, 80);

      return clearScheduledRecalculate;
    }, [recalculateSignal, updateTop]);

    return (
      <>
        <Box
          ref={ref}
          sx={{
            height: "100%",
            maxHeight: getMaxHeightValue(top, additionalMaxHeightOffset),
            overflowY: "auto",
            overflowX: "hidden",
          }}>
          <Component {...(props as T)} />
        </Box>
        {!withoutScrollTopButton && (
          <ScrollTopButton
            threshold={400}
            scrollIntoRef={ref}
          />
        )}
      </>
    );
  };
};

export default withHeightFitContent;
